// src/App.js
import React, { useEffect, useMemo, useState } from "react";
import "./fantasy-ipl.css";
import { db, auth } from "./firebase";
import { ref, onValue, get, update } from "firebase/database";
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
} from "firebase/auth";

import { DraftPage }      from "./pages/DraftPage";
import { DraftRecapPage } from "./pages/DraftRecapPage";
import { SeasonPage }     from "./pages/SeasonPage";
import { StandingsPage }  from "./pages/StandingsPage";
import { LineupPage }     from "./pages/LineupPage";
import { ScoreEntryPage } from "./pages/ScoreEntryPage";
import { WaiverPage }     from "./pages/WaiverPage";
import { PlayoffPage }    from "./pages/PlayoffPage";
import { SummaryPage }    from "./pages/SummaryPage";
import { PlayersPage }    from "./pages/PlayersPage";
import { MatchupPage }    from "./pages/MatchupPage";
import { TradePage }        from "./pages/TradePage";
import { ActivityFeed }     from "./components/ActivityFeed";
import { TeamSettingsPage }   from "./pages/TeamSettingsPage";
import { CommissionerPage }   from "./pages/CommissionerPage";
import { TopNav }             from "./components/TopNav";
import { TeamHeaderCard }   from "./components/TeamHeaderCard";

function randomLeagueCode() {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let s = "L-";
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}
function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

const LEAF_LOGO = `${process.env.PUBLIC_URL}/spinach_logo_filled.png`;
const BrandLogo = ({ size = 26, style = {} }) => (
  <img src={LEAF_LOGO} alt="Carpediemed" height={size} width={size} style={{ objectFit: "contain", ...style }} />
);

const FEATURES = [
  { icon: "⚡", title: "Live Scoring",      desc: "Auto-synced IPL match scores power your fantasy points in real time." },
  { icon: "🎯", title: "Weekly Lineups",    desc: "Pick your 11 each week. Captain earns 2× — choose wisely." },
  { icon: "🔄", title: "Snake Draft",       desc: "Real-time draft room with pick timers and live team tracking." },
  { icon: "📊", title: "Full Season Stats", desc: "Standings, trades, waivers, and playoffs — one complete platform." },
];

// ── Auth screen ─────────────────────────────────────────────────
function AuthScreen() {
  const [showEmail,    setShowEmail]    = useState(false);
  const [authMode,     setAuthMode]     = useState("login");
  const [profileName,  setProfileName]  = useState("");
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [authStatus,   setAuthStatus]   = useState("");

  const loginWithGoogle = async () => {
    setAuthStatus("");
    try { await signInWithPopup(auth, new GoogleAuthProvider()); }
    catch (e) { setAuthStatus(e?.message || "Google sign-in failed."); }
  };
  const login = async () => {
    setAuthStatus("");
    try { await signInWithEmailAndPassword(auth, email.trim(), password); }
    catch (e) { setAuthStatus(e?.message || "Login failed."); }
  };
  const createAccount = async () => {
    setAuthStatus("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (profileName.trim()) await updateProfile(cred.user, { displayName: profileName.trim() });
    } catch (e) { setAuthStatus(e?.message || "Signup failed."); }
  };

  return (
    <div className="landing-page">
      {/* Top bar */}
      <div className="landing-topbar">
        <div className="landing-brand">
          <BrandLogo size={28} />
          <span>carpediemed</span>
        </div>
        <span className="landing-season-badge">IPL 2026</span>
      </div>

      <div className="landing-body">
        {/* Hero left */}
        <div className="landing-hero">
          <div className="landing-eyebrow">carpediemed · IPL 2026</div>
          <h1 className="landing-headline">
            Your league.<br />Your picks.<br />
            <span className="landing-headline-accent">Your glory.</span>
          </h1>
          <p className="landing-subheadline">
            A full-featured fantasy cricket platform built for friends — live scoring,
            weekly lineups, snake drafts, trades, waivers, and playoffs.
          </p>

          <div className="landing-features">
            {FEATURES.map(f => (
              <div key={f.title} className="landing-feature">
                <span className="landing-feature-icon">{f.icon}</span>
                <div>
                  <div className="landing-feature-title">{f.title}</div>
                  <div className="landing-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="landing-stat-row">
            <div className="landing-stat"><span>70</span>IPL matches</div>
            <div className="landing-stat-div" />
            <div className="landing-stat"><span>12</span>Fantasy weeks</div>
            <div className="landing-stat-div" />
            <div className="landing-stat"><span>Real-time</span>Live scoring</div>
          </div>
        </div>

        {/* Auth card right */}
        <div className="landing-auth-wrap">
          <div className="landing-auth-card">
            <div className="landing-auth-header">
              <div className="landing-auth-title">Get Started</div>
              <div className="landing-auth-sub">Create or join a fantasy league</div>
            </div>

            <button className="google-btn" onClick={loginWithGoogle} type="button">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="landing-or"><span>or</span></div>

            {!showEmail ? (
              <button style={{ width: "100%" }} onClick={() => setShowEmail(true)} type="button">
                Continue with Email
              </button>
            ) : (
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                  {["login", "signup"].map(m => (
                    <button key={m} className={authMode === m ? "primary" : ""} onClick={() => setAuthMode(m)} style={{ flex: 1 }} type="button">
                      {m === "login" ? "Log In" : "Sign Up"}
                    </button>
                  ))}
                </div>
                {authMode === "signup" && (
                  <>
                    <label className="label" style={{ display: "block", marginBottom: 6 }}>Display Name</label>
                    <input className="search-box" value={profileName} onChange={e => setProfileName(e.target.value)} style={{ marginBottom: 12 }} />
                  </>
                )}
                <label className="label" style={{ display: "block", marginBottom: 6 }}>Email</label>
                <input className="search-box" value={email} onChange={e => setEmail(e.target.value)} style={{ marginBottom: 12 }} />
                <label className="label" style={{ display: "block", marginBottom: 6 }}>Password</label>
                <input className="search-box" type="password" value={password} onChange={e => setPassword(e.target.value)} style={{ marginBottom: 14 }} />
                <button className="primary" style={{ width: "100%" }} onClick={authMode === "signup" ? createAccount : login} type="button">
                  {authMode === "signup" ? "Create Account" : "Log In"}
                </button>
                <button className="mini" style={{ marginTop: 10, width: "100%" }} onClick={() => setShowEmail(false)} type="button">
                  ← Back
                </button>
              </div>
            )}

            {authStatus && (
              <div style={{ marginTop: 12, fontSize: 13, color: "var(--red)", textAlign: "center" }}>{authStatus}</div>
            )}

            <div className="landing-auth-footer">
              carpediemed · IPL 2026 · Mar 28 – Jun 2026
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard (league select/create) ────────────────────────────
function Dashboard({ user, onOpenLeague }) {
  const [view,          setView]          = useState("leagues"); // "leagues" | "create" | "join"
  const [joinCode,      setJoinCode]      = useState("");
  const [joinStatus,    setJoinStatus]    = useState("");
  const [newName,       setNewName]       = useState("");
  const [newTeams,      setNewTeams]      = useState(6);
  const [newTimer,      setNewTimer]      = useState(60);
  const [createStatus,  setCreateStatus]  = useState("");
  const [myLeagueCodes, setMyLeagueCodes] = useState([]);
  const [leaguesByCode, setLeaguesByCode] = useState({});

  const effectiveDisplayName =
    (user?.displayName && user.displayName.trim()) ||
    (user?.email ? user.email.split("@")[0] : "");

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onValue(ref(db, `userLeagues/${user.uid}`), snap => {
      const val = snap.val() || {};
      setMyLeagueCodes(Object.keys(val).filter(k => val[k]));
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!myLeagueCodes.length) { setLeaguesByCode({}); return; }
    let cancelled = false;
    (async () => {
      const out = {};
      for (const code of myLeagueCodes) {
        try {
          const s = await get(ref(db, `leagues/${code}`));
          if (s.exists()) out[code] = s.val();
        } catch { /* ignore */ }
      }
      if (!cancelled) setLeaguesByCode(out);
    })();
    return () => { cancelled = true; };
  }, [myLeagueCodes]);

  const joinLeague = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return setJoinStatus("Enter a league code.");
    setJoinStatus("Joining…");
    try {
      const snap = await get(ref(db, `leagues/${code}`));
      if (!snap.exists()) return setJoinStatus("League not found.");
      const dn = effectiveDisplayName || "Player";
      await update(ref(db), {
        [`leagues/${code}/members/${user.uid}`]: { uid: user.uid, name: dn, joinedAt: Date.now() },
        [`userLeagues/${user.uid}/${code}`]: true,
      });
      setJoinStatus("");
      onOpenLeague(code);
    } catch (e) { setJoinStatus(e?.message || "Join failed."); }
  };

  const createLeague = async () => {
    const name = newName.trim();
    if (!name) return setCreateStatus("Enter a league name.");
    const code      = randomLeagueCode();
    const teamCount = clamp(Number(newTeams), 2, 12);
    const timerSecs = clamp(Number(newTimer), 10, 300);
    setCreateStatus("Creating…");
    try {
      const dn = effectiveDisplayName || "Host";
      await update(ref(db), {
        [`leagues/${code}`]: {
          code, name, createdAt: Date.now(), ownerUid: user.uid,
          phase: "draft", season: { week: 1 },
          settings: { teamCount, timerSeconds: timerSecs },
          members: { [user.uid]: { uid: user.uid, name: dn, joinedAt: Date.now() } },
          roomCode: code,
        },
        [`userLeagues/${user.uid}/${code}`]: true,
      });
      setNewName("");
      setCreateStatus("");
      onOpenLeague(code);
    } catch (e) { setCreateStatus(e?.message || "Create failed."); }
  };

  const logout = async () => { try { await signOut(auth); } catch { /* ignore */ } };

  const PHASE_LABEL = { draft: "Drafting", season: "Live", complete: "Complete" };
  const PHASE_DOT   = { draft: "var(--gold)", season: "var(--green)", complete: "var(--text-muted)" };

  return (
    <div className="dashboard-page">
      {/* Dashboard top bar */}
      <div className="dashboard-topbar">
        <div className="landing-brand" style={{ fontSize: 16 }}>
          <BrandLogo size={24} />
          carpediemed
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{effectiveDisplayName}</span>
          <button className="mini" onClick={logout} type="button">Sign out</button>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Page title */}
        <div className="dashboard-heading">
          <div>
            <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 900, marginBottom: 2 }}>
              My Leagues
            </h2>
            <p style={{ fontSize: 13, color: "var(--text-dim)" }}>IPL 2026 · Mar 28 – May 24</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setView("join")} type="button">Join League</button>
            <button className="primary" onClick={() => setView("create")} type="button">＋ Create</button>
          </div>
        </div>

        {/* My Leagues */}
        {view === "leagues" && (
          <>
            {myLeagueCodes.length === 0 ? (
              <div className="dashboard-empty">
                <div className="dashboard-empty-icon"><BrandLogo size={44} /></div>
                <div className="dashboard-empty-title">No leagues yet</div>
                <div className="dashboard-empty-sub">Create a private league for your friends or join one with an invite code.</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
                  <button className="primary" onClick={() => setView("create")} type="button">Create a League</button>
                  <button onClick={() => setView("join")} type="button">Join with Code</button>
                </div>
              </div>
            ) : (
              <div className="dashboard-league-list">
                {myLeagueCodes.map(code => {
                  const lg = leaguesByCode[code];
                  const phase = lg?.phase || "draft";
                  const memberCount = lg?.members ? Object.keys(lg.members).length : null;
                  return (
                    <div key={code} className="dashboard-league-card" onClick={() => onOpenLeague(code)}>
                      <div className="dlc-left">
                        <div className="dlc-icon">{(lg?.name || "?")[0].toUpperCase()}</div>
                        <div>
                          <div className="dlc-name">{lg?.name || code}</div>
                          <div className="dlc-meta">
                            <span className="dlc-code">{code}</span>
                            {memberCount && <span>{memberCount} member{memberCount !== 1 ? "s" : ""}</span>}
                            {lg?.settings?.teamCount && <span>{lg.settings.teamCount} teams</span>}
                          </div>
                        </div>
                      </div>
                      <div className="dlc-right">
                        <span className="dlc-phase" style={{ color: PHASE_DOT[phase] || "var(--text-dim)" }}>
                          ● {PHASE_LABEL[phase] || phase}
                        </span>
                        <span className="dlc-arrow">›</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Join */}
        {view === "join" && (
          <div className="dashboard-form-card">
            <div className="dashboard-form-header">
              <button className="mini" onClick={() => setView("leagues")} type="button">← Back</button>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Join a League</span>
            </div>
            <label className="label" style={{ display: "block", marginBottom: 8 }}>League Invite Code</label>
            <input
              className="search-box"
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.toUpperCase())}
              placeholder="e.g. L-ABCD12"
              style={{ letterSpacing: "0.12em", fontWeight: 700, marginBottom: 14, fontSize: 15 }}
              onKeyDown={e => e.key === "Enter" && joinLeague()}
              autoFocus
            />
            <button className="primary" style={{ width: "100%" }} onClick={joinLeague} type="button">
              Join League
            </button>
            {joinStatus && (
              <div style={{ marginTop: 10, fontSize: 13, color: "var(--red)" }}>{joinStatus}</div>
            )}
          </div>
        )}

        {/* Create */}
        {view === "create" && (
          <div className="dashboard-form-card">
            <div className="dashboard-form-header">
              <button className="mini" onClick={() => setView("leagues")} type="button">← Back</button>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Create a League</span>
            </div>
            <label className="label" style={{ display: "block", marginBottom: 8 }}>League Name</label>
            <input className="search-box" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="e.g. The Royal XI Fantasy League" style={{ marginBottom: 16 }} autoFocus />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
              <div>
                <label className="label" style={{ display: "block", marginBottom: 8 }}>Number of Teams</label>
                <input type="number" min="2" max="12" value={newTeams}
                  onChange={e => setNewTeams(Number(e.target.value))}
                  style={{ width: "100%", textAlign: "center" }} />
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>2 – 12 teams</div>
              </div>
              <div>
                <label className="label" style={{ display: "block", marginBottom: 8 }}>Draft Pick Timer</label>
                <input type="number" min="10" max="300" value={newTimer}
                  onChange={e => setNewTimer(Number(e.target.value))}
                  style={{ width: "100%", textAlign: "center" }} />
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>seconds per pick</div>
              </div>
            </div>

            <button className="primary" style={{ width: "100%" }} onClick={createLeague} type="button">
              Create League
            </button>
            {createStatus && (
              <div style={{ marginTop: 10, fontSize: 13, color: "var(--gold)" }}>{createStatus}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────────────
export default function App() {
  const [authLoading,       setAuthLoading]       = useState(true);
  const [user,              setUser]              = useState(null);
  const [activeLeagueCode,  setActiveLeagueCode]  = useState("");
  const [tab,               setTab]               = useState("matchup");
  const [lineupEditTeam,    setLineupEditTeam]    = useState(null);
  const [leagueData,        setLeagueData]        = useState(null);
  const [roomData,          setRoomData]          = useState(null);
  const [copied,            setCopied]            = useState(false);

  // Read ?code=XXXX from URL on first load
  const urlCode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("code")?.trim().toUpperCase() || "";
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u || null);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!activeLeagueCode) { setLeagueData(null); setRoomData(null); return; }
    const code = activeLeagueCode.toUpperCase();
    const u1 = onValue(ref(db, `leagues/${code}`), s => setLeagueData(s.val() || null));
    const u2 = onValue(ref(db, `rooms/${code}`),   s => setRoomData(s.val() || null));
    return () => { u1(); u2(); };
  }, [activeLeagueCode]);

  const openLeague = async (code) => {
    const c = code.trim().toUpperCase();
    setActiveLeagueCode(c);
    // Default to matchup if season started, draft if still drafting
    try {
      const snap = await get(ref(db, `leagues/${c}/phase`));
      setTab(snap.val() === "season" ? "matchup" : "draft");
    } catch {
      setTab("matchup");
    }
    const url = new URL(window.location.href);
    url.searchParams.set("code", c);
    window.history.replaceState({}, "", url.toString());
  };

  const closeLeague = () => {
    setActiveLeagueCode("");
    setLeagueData(null);
    setRoomData(null);
    // Remove code from URL
    const url = new URL(window.location.href);
    url.searchParams.delete("code");
    window.history.replaceState({}, "", url.toString());
  };

  // Auto-open league from URL param ?code=L-XXXX
  useEffect(() => {
    if (user && urlCode && !activeLeagueCode) {
      setActiveLeagueCode(urlCode);
    }
  }, [user, urlCode]); // eslint-disable-line

  const copyInviteLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("code", normalizedCode);
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isDraftOver    = leagueData?.phase === "season" || leagueData?.phase === "complete";
  const normalizedCode = useMemo(() => activeLeagueCode.trim().toUpperCase(), [activeLeagueCode]);

  // ── Loading ──
  if (authLoading) {
    return (
      <div className="auth-screen">
        <div className="spinner" />
        <div style={{ color: "var(--text-dim)" }}>Loading…</div>
      </div>
    );
  }

  // ── Not signed in ──
  if (!user) return <AuthScreen />;

  // ── No league selected: show dashboard ──
  if (!activeLeagueCode) {
    return <Dashboard user={user} onOpenLeague={openLeague} initialCode={urlCode} />;
  }

  // ── League view ──
  const effectiveDisplayName =
    (user?.displayName && user.displayName.trim()) ||
    (user?.email ? user.email.split("@")[0] : "");

  return (
    <div className="app">
      {/* ESPN-style site header */}
      <div className="site-header">
        <div className="site-header-logo">
          <BrandLogo size={24} />
          carpediemed
        </div>
        <div className="site-header-right">
          <span style={{
            fontSize: 10, fontFamily: "monospace", letterSpacing: "0.08em",
            color: "var(--gold)", background: "rgba(245,166,35,0.1)",
            padding: "2px 7px", borderRadius: 4,
          }}>{normalizedCode}</span>
          {isDraftOver && <span className="pill green" style={{ fontSize: 9 }}>Season Live</span>}
          <button
            className="mini"
            onClick={copyInviteLink}
            type="button"
            style={{ color: copied ? "var(--green)" : "var(--text-dim)" }}
          >
            {copied ? "✓ Copied!" : "📋 Invite"}
          </button>
          <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{effectiveDisplayName}</span>
          <button className="mini" onClick={closeLeague} type="button">← Leagues</button>
        </div>
      </div>

      {/* ESPN-style league nav */}
      <TopNav
        tab={tab}
        setTab={setTab}
        disabled={false}
        leagueCode={normalizedCode}
        isDraftOver={isDraftOver}
      />

      {/* Team header card — ESPN team overview strip */}
      {isDraftOver && (
        <TeamHeaderCard leagueCode={normalizedCode} onTabChange={setTab} />
      )}

      {tab === "draft"     && !isDraftOver && <DraftPage      leagueCode={normalizedCode} />}
      {tab === "draft"     &&  isDraftOver && <DraftRecapPage leagueCode={normalizedCode} room={roomData} league={leagueData} />}
      {tab === "matchup"   && <MatchupPage    leagueCode={normalizedCode} onEditLineup={(team) => { setLineupEditTeam(team); setTab("lineup"); }} />}
      {tab === "lineup"    && <LineupPage     leagueCode={normalizedCode} initialEditTeam={lineupEditTeam} onMount={() => setLineupEditTeam(null)} />}
      {tab === "players"   && <PlayersPage    league={leagueData} room={roomData} />}
      {tab === "waiver"    && <WaiverPage     leagueCode={normalizedCode} />}
      {tab === "trades"    && <TradePage      leagueCode={normalizedCode} />}
      {tab === "myteam"       && <TeamSettingsPage  leagueCode={normalizedCode} />}
      {tab === "commissioner" && <CommissionerPage   leagueCode={normalizedCode} />}
      {tab === "season"       && <SeasonPage         leagueCode={normalizedCode} />}
      {tab === "standings" && <StandingsPage  leagueCode={normalizedCode} />}
      {tab === "playoffs"  && <PlayoffPage    league={leagueData} />}
      {tab === "summary"   && <SummaryPage    league={leagueData} room={roomData} />}
      {tab === "scores"    && <ScoreEntryPage leagueCode={normalizedCode} />}

      {/* Activity feed sidebar — show on main tabs */}
      {["matchup","season","standings"].includes(tab) && normalizedCode && (
        <div className="panel" style={{ marginTop: 12 }}>
          <div className="panel-head">
            <div style={{ fontWeight: 700, fontSize: 14 }}>📣 League Activity</div>
          </div>
          <ActivityFeed leagueCode={normalizedCode} maxItems={20} compact={true} />
        </div>
      )}
    </div>
  );
}
